import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL } from '@sorare/core/src/atoms/typography';
import type { Variant } from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { parseRankingRewards } from '@sorare/core/src/lib/rewards';

import { CompactSo5Lineup } from 'components/lineup/CompactSo5Lineup';
import { hasGamesStarted, isFixtureOpened } from 'lib/so5';

import { RankRewardTitle } from '../CumulativeRankRewards/RankRewardTitle';
import { RewardRange, RewardRangeVariant } from '../RewardRange';
import { RankReward } from './RankReward';
import type {
  RankRewards_so5Leaderboard,
  RankRewards_so5Ranking,
} from './__generated__/index.graphql';
import { RankRow } from '../CumulativeRankRewards/CumulativeRankRow';

export type Props = {
  so5Leaderboard: RankRewards_so5Leaderboard;
  so5Ranking?: RankRewards_so5Ranking;
  overview?: boolean;
  onClickRanking?: (contender: string) => void;
  rewardVariant?: Variant;
  showAppearances?: boolean;
} & Pick<
  ComponentProps<typeof RankReward>,
  'withSeparator' | 'selectedContender' | 'hideRewards' | 'hideScoreDistance'
>;

export const RankRewards = ({
  so5Leaderboard,
  so5Ranking,
  overview,
  onClickRanking,
  withSeparator,
  rewardVariant,
  selectedContender,
  showAppearances = true,
  hideRewards,
  hideScoreDistance,
}: Props) => {
  const { so5Fixture, rewardsConfig } = so5Leaderboard;

  const isUpcoming = isFixtureOpened(so5Fixture);
  const hasLineupGamesStarted = so5Ranking
    ? hasGamesStarted(so5Ranking?.so5Lineup)
    : !isUpcoming;

  const parsedRankRewards = parseRankingRewards(rewardsConfig.ranking || []);

  const myRankPct = so5Ranking?.eligibleRewards?.reduce<number | null>(
    (prev, curr) => {
      if (!prev) return curr.rankPct;
      if (!curr.rankPct) return prev;
      return prev < curr.rankPct ? prev : curr.rankPct;
    },
    null
  );

  const myRangeIndex = so5Ranking
    ? parsedRankRewards.findIndex(
        rankReward =>
          ((so5Ranking.ranking || 0) >= (rankReward.startRank || 0) &&
            (so5Ranking.ranking || 0) <= (rankReward.endRank || 0)) ||
          (myRankPct && myRankPct === rankReward.endPct)
      )
    : -1;

  const isMyRankingNotRewarded = myRangeIndex === -1;
  const lastRange = parsedRankRewards[parsedRankRewards.length - 1];

  const hideRankings = isUpcoming || !hasLineupGamesStarted || !showAppearances;

  const upcomingRewardRangeVariant = overview
    ? RewardRangeVariant.OVERVIEW_ROW
    : RewardRangeVariant.ROW;
  const rewardRangeVariant = hideRankings
    ? upcomingRewardRangeVariant
    : RewardRangeVariant.BLOCK;

  return (
    <Vertical gap={withSeparator ? 0 : 2}>
      {parsedRankRewards.map((rankReward, i) => {
        const isMyNextRange = myRangeIndex ? i === myRangeIndex - 1 : false;
        const isLastRange = i === parsedRankRewards.length - 1;

        return (
          <RankReward
            key={rankReward.startRank || rankReward.startPct}
            so5Leaderboard={so5Leaderboard}
            so5RewardConfig={rankReward}
            hideRankings={hideRankings}
            so5Ranking={so5Ranking}
            overview={!!hasLineupGamesStarted && !!overview && !isUpcoming}
            isMyRange={myRangeIndex === i}
            isMyNextRange={
              isMyNextRange || (isMyRankingNotRewarded && isLastRange)
            }
            rewardVariant={rewardVariant}
            rewardRangeVariant={rewardRangeVariant}
            onClickRanking={onClickRanking}
            selectedContender={selectedContender}
            withSeparator={withSeparator}
            hideRewards={hideRewards}
            hideScoreDistance={hideScoreDistance}
          />
        );
      })}
      {!!isMyRankingNotRewarded &&
        !hideRankings &&
        !!so5Ranking &&
        !!lastRange && (
          <RewardRange
            variant={rewardRangeVariant}
            title={
              <RankRewardTitle
                startRank={lastRange.endRank}
                startPct={lastRange.endPct}
                endRank={so5Leaderboard.so5LineupsCount}
                endPct={null}
              />
            }
            withSeparator={withSeparator}
          >
            <RankRow
              key={so5Ranking.ranking}
              ranking={so5Ranking}
              isMe
              isSelected={
                selectedContender ===
                so5Ranking.so5Lineup.so5LeaderboardContender.slug
              }
              onClick={() =>
                onClickRanking?.(
                  so5Ranking.so5Lineup.so5LeaderboardContender.slug
                )
              }
              collapsible={false}
              startOpen
            >
              {() => <CompactSo5Lineup so5Lineup={so5Ranking.so5Lineup} />}
            </RankRow>
          </RewardRange>
        )}
      {!lastRange && (
        <LabelL>
          <FormattedMessage
            id="RankRewards.noRewards"
            defaultMessage="No GW Rewards"
          />
        </LabelL>
      )}
    </Vertical>
  );
};

RankRewards.fragments = {
  so5Leaderboard: gql`
    fragment RankRewards_so5Leaderboard on So5Leaderboard {
      slug
      mainRarityType
      so5LineupsCount
      so5Fixture {
        slug
        aasmState
      }
      rewardsConfig {
        ranking {
          id
          ...RankReward_so5RewardConfig
          ...parseRankingRewards_so5RewardConfig
        }
      }
      ...RankReward_so5Leaderboard
    }
    ${parseRankingRewards.fragments.so5RewardConfig}
    ${RankReward.fragments.so5RewardConfig}
    ${RankReward.fragments.so5Leaderboard}
  ` as TypedDocumentNode<RankRewards_so5Leaderboard>,
  so5Ranking: gql`
    fragment RankRewards_so5Ranking on So5Ranking {
      id
      ranking
      eligibleRewards {
        id
        rankPct
      }
      so5Lineup {
        id
        so5LeaderboardContender {
          slug
        }
        ...hasGamesStarted_so5Lineup
        ...CompactSo5Lineup_so5Lineup
      }
      ...RankReward_so5Ranking
      ...RankRow_userRankingInterface
    }
    ${RankReward.fragments.so5Ranking}
    ${hasGamesStarted.fragments.so5Lineup}
    ${CompactSo5Lineup.fragments.so5Lineup}
    ${RankRow.fragments.userRankingInterface}
  ` as TypedDocumentNode<RankRewards_so5Ranking>,
};
